import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../../../../core/services/utils.service';
import { SubscriptionsFilterTagName } from '../subscriptions.model';
import { Tag } from '../../../kkms/kkms-page-filter/kkms-page-filter.types';
import { SubscriptionsApiService } from '../../../../core/modules/subscriptions/subscriptions-api.service';
import { SubscriptionFilter } from '../../../../core/modules/subscriptions/subscriptions.types';

export interface TagKeyValue {
  key: SubscriptionsFilterTagName;
  value: Tag;
}

export type SubscriptionsTagsFilter = {
  [key in SubscriptionsFilterTagName]: Tag;
};

type OptionsFlags<Type> = {
  [Property in keyof Type]: Tag;
};

@UntilDestroy()
@Component({
  selector: 'tax-subscriptions-filter',
  templateUrl: 'subscriptions-filter.component.html',
  styleUrls: ['subscriptions-filter.component.sass'],
})
export class SubscriptionsFilterComponent implements OnInit {
  @Output() applyFilter: EventEmitter<SubscriptionFilter> = new EventEmitter<SubscriptionFilter>();

  readonly objectInOriginalOrder = this.utilsService.objectInOriginalOrder;

  readonly searchInput = new FormControl<string>('', {
    nonNullable: true,
  });

  readonly subscriptionsTags: SubscriptionsTagsFilter = {
    active: {
      count: 0, selected: false, name: 'tariffs.subscription.state.active',
    },
    inactive: {
      count: 0, selected: false, name: 'tariffs.subscription.state.inactive',
    },
    noSubscription: {
      count: 0, selected: false, name: 'tariffs.subscription.state.noSubscription',
    },
    total: {
      count: 0, selected: false, name: 'tariffs.subscription.state.total',
    },
  };

  constructor(
    private subscriptionsApiService: SubscriptionsApiService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
  ) {
  }

  ngOnInit(): void {
    this.getStatusesCount();
    this.getStatusesTranslates();
    this.emitFilterOnInputChange();
  }

  selectTag(tag: TagKeyValue): void {
    this.toggleTagsSelectedState(tag);

    const statusFilter = tag.value.selected && tag.key !== 'total'
      ? {
        status: tag.key,
      }
      : {};
    this.applyFilter.emit({
      ...statusFilter,
      criteria: this.searchInput.value,
    });
  }

  private deselectAllTags(): void {
    Object.values(this.subscriptionsTags)
      .forEach((tag) => {
        tag.selected = false;
      });
  }

  private emitFilterOnInputChange(): void {
    this.searchInput.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(this.utilsService.INPUT_DEBOUNCE_TIME),
        untilDestroyed(this),
      )
      .subscribe((inputValue: string) => {
        const selectedFilter = Object.entries(this.subscriptionsTags)
          .find(([status, filterTag]) => filterTag.selected);

        // @ts-ignore
        this.applyFilter.emit({
          criteria: inputValue,
          ...(selectedFilter && {
            status: selectedFilter[0],
          }),
        });
      });
  }

  private getStatusesCount(): void {
    this.subscriptionsApiService.fetchFilterCounters()
      .pipe(untilDestroyed(this))
      .subscribe((tagsCounters) => {
        Object.entries(tagsCounters)
          // @ts-ignore
          .forEach(([tagName, count]: [SubscriptionsFilterTagName, number]) => {
            this.subscriptionsTags[tagName].count = count;
          });
      });
  }

  private getStatusesTranslates(): void {
    this.translateService.get(Object.values(this.subscriptionsTags)
      .map(tag => tag.name))
      .pipe(untilDestroyed(this))
      .subscribe((translations) => {
        Object.values(this.subscriptionsTags)
          .map(tag => ({
            ...tag,
            name: translations[tag.name],
          }));
      });
  }

  private toggleTagsSelectedState(tag: TagKeyValue): void {
    if (!tag.value.selected) {
      this.deselectAllTags();
    }
    tag.value.selected = !tag.value.selected;
  }
}
